import React, { Ref } from 'react';

// material-ui
import {
    Card,
    CardContent,
    CardContentProps,
    CardHeader,
    CardHeaderProps,
    CardProps,
    Divider,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// project imports
import { KeyedObject } from 'src/types';

// constant
const headerSX = {
    '& .MuiCardHeader-action': { mr: 0 },
};

// ==============================|| CUSTOM MAIN CARD ||============================== //

export interface MainCardProps extends KeyedObject {
    border?: boolean;
    boxShadow?: boolean;
    children: React.ReactNode | string;
    style?: React.CSSProperties;
    content?: boolean;
    className?: string;
    contentClass?: string;
    contentSX?: CardContentProps['sx'];
    darkTitle?: boolean;
    sx?: CardProps['sx'];
    secondary?: CardHeaderProps['action'];
    shadow?: string;
    elevation?: number;
    title?: React.ReactNode | string;
}

const MainCard = React.forwardRef(
    (
        {
            border = true,
            boxShadow,
            children,
            content = true,
            contentClass = '',
            contentSX = {},
            darkTitle,
            secondary,
            shadow,
            sx = {},
            title,
            ...others
        }: MainCardProps,
        ref: Ref<HTMLDivElement>
    ) => {
        const theme = useTheme();

        const cardStyles = {
            border: border ? '1px solid #FEE380' : 'none',
            borderColor: '#FEE380',
            ':hover': {
                boxShadow: boxShadow
                    ? shadow ||
                      (theme.palette.mode === 'dark'
                          ? theme.customShadows.blue10
                          : theme.customShadows.darkBlue8)
                    : 'inherit',
            },
            ...sx,
        };

        return (
            <Card ref={ref} {...others} sx={cardStyles}>
                {/* card header and action */}
                {!darkTitle && title && (
                    <CardHeader sx={headerSX} title={title} action={secondary} />
                )}
                {darkTitle && title && (
                    <CardHeader
                        sx={headerSX}
                        title={<Typography variant="h3">{title}</Typography>}
                        action={secondary}
                    />
                )}

                {/* content & header divider */}
                {title && <Divider />}

                {/* card content */}
                {content && (
                    <CardContent sx={contentSX} className={contentClass}>
                        {children}
                    </CardContent>
                )}
                {!content && children}
            </Card>
        );
    }
);

export default MainCard;
